import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Layout, Space, Table, Tag, Typography } from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { Header } from "components";
import { useNavigate } from "react-router-dom";
import { listRedemptions } from "services/breakage-service";
import { formatAmountMinor } from "views/breakage/breakage-money";

const { Content } = Layout;
const { Text, Title } = Typography;
const PAGE_SIZE = 50;

const STATE_LABELS = {
  REDEMPTION_REQUESTED: "Solicitado",
  REDEMPTION_PROCESSING: "Em processamento",
  REDEEMED: "Concluído",
  REJECTED: "Rejeitado",
  REDEMPTION_FAILED: "Falha no processamento",
  RECONCILIATION_PENDING: "Em reconciliação",
};

const STATE_COLORS = {
  REDEMPTION_REQUESTED: "blue",
  REDEMPTION_PROCESSING: "processing",
  REDEEMED: "success",
  REJECTED: "error",
  REDEMPTION_FAILED: "error",
  RECONCILIATION_PENDING: "warning",
};

const displayState = (item) => {
  const state = item.technicalState || item.businessState;
  return STATE_LABELS[state] || state || "Status indisponível";
};

const BreakageRedemptionsScreen = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursorStack, setCursorStack] = useState([null]);
  const [page, setPage] = useState({ hasMore: false, nextCursor: null });
  const [pageIndex, setPageIndex] = useState(0);

  const load = useCallback(async (cursor) => {
    setLoading(true);
    setError(null);
    const result = await listRedemptions({ limit: PAGE_SIZE, cursor });
    if (result?.ok) {
      setItems(result.data?.items || []);
      setPage(result.data?.page || { hasMore: false, nextCursor: null });
    } else {
      setItems([]);
      setError(result?.status === 403
        ? "Seu usuário não tem acesso aos resgates desta empresa."
        : "Não foi possível carregar os resgates. Tente novamente.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(cursorStack[pageIndex]); }, [cursorStack, pageIndex, load]);

  const nextPage = () => {
    if (!page.hasMore || !page.nextCursor) return;
    const next = [...cursorStack.slice(0, pageIndex + 1), page.nextCursor];
    setCursorStack(next);
    setPageIndex(pageIndex + 1);
  };

  const previousPage = () => setPageIndex((index) => Math.max(0, index - 1));

  const columns = [
    {
      title: "Solicitado em",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => value ? new Date(value).toLocaleString("pt-BR") : "—",
    },
    {
      title: "Identificador",
      dataIndex: "redemptionId",
      key: "redemptionId",
      render: (value) => <Text copyable={{ text: value }}>{value}</Text>,
    },
    {
      title: "Valor",
      dataIndex: "redeemedAmount",
      key: "amount",
      render: (value) => value ? formatAmountMinor(value) : "A apurar",
    },
    {
      title: "Status",
      key: "status",
      render: (_, item) => <Tag color={STATE_COLORS[item.technicalState || item.businessState]}>{displayState(item)}</Tag>,
    },
    {
      title: "Atualizado em",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => value ? new Date(value).toLocaleString("pt-BR") : "—",
    },
  ];

  return (
    <Layout>
      <Header title="Resgates" />
      <Content style={{ padding: 24 }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
              <div>
                <Title level={4} style={{ margin: 0 }}>Acompanhamento de resgates</Title>
                <Text type="secondary">Consulte o andamento dos pedidos desta empresa.</Text>
              </div>
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/breakage")}>
                  Voltar ao resumo
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => load(cursorStack[pageIndex])} loading={loading}>
                  Atualizar
                </Button>
              </Space>
            </Space>
            {error && <Alert type="error" showIcon message={error} />}
            {!loading && !error && items.length === 0 && (
              <Alert type="info" showIcon message="Nenhum resgate encontrado" description="Quando houver um pedido de resgate, ele aparecerá nesta lista." />
            )}
            <Table
              rowKey="redemptionId"
              columns={columns}
              dataSource={items}
              loading={loading}
              pagination={false}
              locale={{ emptyText: "" }}
              scroll={{ x: 850 }}
            />
            <Space style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button disabled={pageIndex === 0 || loading} onClick={previousPage}>Anterior</Button>
              <Text>Página {pageIndex + 1}</Text>
              <Button disabled={!page.hasMore || loading} onClick={nextPage}>Próxima</Button>
            </Space>
          </Space>
        </Card>
      </Content>
    </Layout>
  );
};

export default BreakageRedemptionsScreen;
